import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import Image from "next/image";
import { scrollToSection } from "@/lib/utils";

const ServicesMobileSection: React.FC = () => {
  const [animationProgress, setAnimationProgress] = useState(0);

  useEffect(() => {
    let animationFrame: number;
    let direction = 1;
    let progress = 0;

    const animate = () => {
      progress += 0.002 * direction;

      if (progress >= 1) {
        progress = 1;
        direction = -1;
        setAnimationProgress(progress);
        setTimeout(() => {
          animationFrame = requestAnimationFrame(animate);
        }, 1500);
        return;
      } else if (progress <= 0) {
        progress = 0;
        direction = 1;
        setAnimationProgress(progress);
        setTimeout(() => {
          animationFrame = requestAnimationFrame(animate);
        }, 1500);
        return;
      }

      setAnimationProgress(progress);
      animationFrame = requestAnimationFrame(animate);
    };

    animationFrame = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(animationFrame);
    };
  }, []);

  const Services = [
    "Product Design",
    "UI/UX Design",
    "Web Development",
    "Brand Identity",
    "IOS & Android Apps",
    "Prototyping & Testing",
    "Design Systems",
  ];

  const mainServices = Services.slice(0, Services.length - 3);
  const lastServices = Services.slice(-3);

  return (
    <section className="min-w-full px-4">
      <div className="relative min-w-full sm:hidden max-w-full my-6 mb-0 rounded-3xl h-[500px] overflow-hidden">
        {/* Background image */}
        <motion.div
          className="absolute inset-0 w-full h-[150%] overflow-hidden scale-[106%]"
          style={{
            y: `${(1 - animationProgress) * -33}%`,
          }}
        >
          <Image
            src="/images/services.png"
            alt="Services"
            fill
            className="object-cover object-center"
            priority
          />
        </motion.div>

        {/* Overlay */}
        <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px]" />

        {/* Content */}
        <div className="relative z-10 flex flex-col h-full px-4 py-8">
          <h2 className="text-4xl text-white mb-8 text-center">Our Services</h2>

          <div className="flex flex-1 flex-col gap-6">
            {/* First two columns */}
            <div className="flex justify-between gap-4">
              <div className="flex flex-col gap-4 items-start">
                {mainServices
                  .filter((_, idx) => idx % 2 === 0)
                  .map((service, index) => (
                    <motion.div
                      key={service}
                      className="rounded-full text-sm whitespace-nowrap border border-white/50 text-primary bg-white/90 backdrop-blur-[10px] px-4 py-2"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{
                        duration: 0.4,
                        delay: index * 0.1,
                        ease: "easeOut",
                      }}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      {service}
                    </motion.div>
                  ))}
              </div>

              <div className="flex flex-col text-sm gap-4 items-end">
                {mainServices
                  .filter((_, idx) => idx % 2 !== 0)
                  .map((service, index) => (
                    <motion.div
                      key={service}
                      className="rounded-full border whitespace-nowrap border-white/50 text-primary bg-white/90 backdrop-blur-[10px] px-4 py-2"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{
                        duration: 0.4,
                        delay: index * 0.1,
                        ease: "easeOut",
                      }}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      {service}
                    </motion.div>
                  ))}
              </div>
            </div>

            {/* Center last three services */}
            <div className="flex flex-col text-sm  items-center gap-4 mt-6">
              {lastServices.map((service, index) => (
                <motion.div
                  key={service}
                  className="rounded-full border whitespace-nowrap border-white/50 text-primary bg-white/90 backdrop-blur-[10px] px-6 py-2"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.4,
                    delay: index * 0.2,
                    ease: "easeOut",
                  }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {service}
                </motion.div>
              ))}
            </div>
          </div>

          {/* Arrow button */}
          <div
            className="absolute bottom-6 right-6"
            onClick={() => scrollToSection("what-we-do")}
          >
            <div className="group bg-white h-12 w-12 cursor-pointer relative -top-2 font-extralight text-primary rounded-full flex items-center justify-center overflow-hidden">
              <div className="absolute inset-0 rounded-full bg-primary-sky transform translate-x-[-100%] translate-y-[100%] transition-transform duration-300 ease-in-out group-hover:translate-x-0 group-hover:translate-y-0" />
              <ArrowUpRight
                className="text-black absolute transform transition-transform duration-300 ease-in-out group-hover:translate-x-[100%] group-hover:translate-y-[-100%] h-12"
                strokeWidth={1.4}
              />
              <ArrowUpRight
                className="text-black absolute transform translate-x-[-100%] translate-y-[100%] transition-transform duration-300 ease-in-out group-hover:translate-x-0 group-hover:translate-y-0 h-12"
                strokeWidth={1.4}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesMobileSection;
